/**
 * LA PARTITA CONTRO IL BOT — le scelte del foglio e il CORPO di `POST /matches/practice`.
 *
 * Puro, come `queueMode.ts`: il corpo che va sul filo lo decide una funzione che un banco puo'
 * eseguire, non un oggetto scritto a mano dentro un componente.
 *
 * [Reference «Anello che respira», Luca, I-277, L-2606 (a), 30/09 — tavola `Foglio-bot.dc.html`.]
 *  - **Livello**: Facile = `qbot_easy` (bot L0), Forte = `qbot` (bot L2). Due, non tre: un terzo
 *    livello vorrebbe un bot L1 in QGammon, ed e' riservato a Luca (contratto `ranked-bg`, §4).
 *    ⚠️ Che `qbot_easy` esista su qgammon-stg e' [NON MISURATO] (qgammon-orcr, 30/09 19:54Z): se non
 *    c'e' il server risponde `404 bot_not_found` [C QGammon `origin/main@8907279`, `rest.ts`
 *    `createPracticeMatch`], e il foglio lo dice («questo livello non e' disponibile»), non un
 *    generico «impossibile avviare».
 *  - **Lunghezza**: 1 / 3 / 5 punti, come in «Gioca».
 *  - **Orologio**: CON o SENZA, predefinito SENZA. «Con» NON manda numeri: si omette `timeControl` e
 *    vale il default del server (oggi 600/15 per la pratica, `rest.ts:PracticeMatchSchema`) — la
 *    stessa disciplina di L-722 sulla coda: «con orologio» significa «quello che dice il server».
 *    «Senza» manda il ramo della union `{unlimited: true}`, un booleano LETTERALE.
 *
 * 📌 Prima di questo foglio una partita col bot partiva sempre senza `timeControl`, cioe' CON
 * l'orologio del server, e nessuno lo sceglieva [M `BackgammonLobby.playVsBot` @ c0071db]. La scheda
 * «Gioca» resta cosi' (non si tocca in questo giro).
 */

export type LivelloBot = 'facile' | 'forte' | 'sperimentale';
export type LunghezzaMatch = 1 | 3 | 5;

/**
 * Il `botUsername` di ciascun livello: sono gli username degli utenti-bot di QGammon.
 *
 * 🧪 **`sperimentale` → `qbot_multi5` (Multi5, BACKGAMMON-COACH / DIFFICOLTA tappa A2, 31/10)** esiste in questa
 * mappa SOLO se `VITE_FEATURE_BACKGAMMON_BOT_MULTI5_ENABLED` e' `'true'`. A flag spento la mappa ha DUE voci e la
 * chiave `sperimentale` non c'e': non e' una voce spenta, non e' scritta. Il confronto e' LETTERALE e sta QUI, nel
 * punto in cui la voce si monta, perche' e' quello che il bundler piega: in produzione la stringa `qbot_multi5`
 * sparisce dalla build (`tools/verify-bot-livelli.ts`). ⛔ Non spostarlo in `env.ts` ne' dentro una funzione: una
 * stringa fuori dal ramo piegato resterebbe nel bundle di produzione.
 * ⚠️ Solo lo username come identita': niente di Multi5 (il suo motore e' GPL, ADR-0261) entra nel client.
 * ⚠️ Se un giorno si RICORDA la scelta del livello (oggi e' `useState` del foglio), un `sperimentale` salvato a flag
 * spento deve ricadere su `facile`: qui la chiave non c'e', e `corpoPartitaBot` rifiuta un livello assente.
 */
export const BOT_DEL_LIVELLO: Partial<Record<LivelloBot, string>> = {
  facile: 'qbot_easy',
  forte: 'qbot',
  ...(import.meta.env.VITE_FEATURE_BACKGAMMON_BOT_MULTI5_ENABLED === 'true'
    ? { sperimentale: 'qbot_multi5' }
    : {}),
};

/**
 * Il marcatore DOM della nota del livello sperimentale. Come `qbot_multi5`, esiste solo dentro il ramo del confronto
 * letterale: a flag spento e' `null` e la stringa non e' nel bundle (`tools/verify-bot-livelli.ts` la cerca).
 */
export const NOTA_SPERIMENTALE_TESTID: string | null =
  import.meta.env.VITE_FEATURE_BACKGAMMON_BOT_MULTI5_ENABLED === 'true' ? 'bot-sperimentale-nota' : null;

/** I livelli che il foglio offre, nell'ordine della mappa (Facile, Forte, poi Sperimentale se c'e'). */
export function livelliOfferti(): LivelloBot[] {
  return Object.keys(BOT_DEL_LIVELLO) as LivelloBot[];
}

export interface SceltePartitaBot {
  livello: LivelloBot;
  matchLength: LunghezzaMatch;
  /** `true` = con l'orologio del server; `false` = senza orologio. */
  orologio: boolean;
}

/** Le scelte con cui il foglio si apre: Facile, 3 punti, senza orologio. */
export const SCELTE_INIZIALI: SceltePartitaBot = {
  livello: 'facile',
  matchLength: 3,
  orologio: false,
};

/**
 * Il corpo esatto di `POST /matches/practice`.
 * ⛔ Con l'orologio `timeControl` NON c'e': nessun numero scritto nel client.
 */
export function corpoPartitaBot(s: SceltePartitaBot): Record<string, unknown> {
  const botUsername = BOT_DEL_LIVELLO[s.livello];
  // Un livello che la mappa non ha (`sperimentale` a flag spento) NON ripiega su un altro bot: si ferma.
  if (botUsername === undefined) throw new Error(`livello bot assente: ${s.livello}`);
  return {
    botUsername,
    matchLength: s.matchLength,
    ...(s.orologio ? {} : { timeControl: { unlimited: true } }),
  };
}
